import { OtherSessionsActiveComponent } from './../other-sessions-active/other-sessions-active.component';
import { PasswordChangeRequiredComponent } from './../password-change-required/password-change-required.component';
import { RobRequiredComponent } from './../rob-required/rob-required.component';
import { AuthService } from './../service/auth.service';
import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { AuthActions } from './action-types';
import { filter, map, tap } from 'rxjs/operators';
import { NavigationEnd, NavigationStart, Router, ActivatedRoute } from '@angular/router';
import { MatDialog, MatDialogConfig, MatDialogRef, MatDialogState } from '@angular/material/dialog';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { Route, TrackingType } from 'src/app/model/session-tracking/tracking-type';
import { DatePipe, Location } from '@angular/common';
import { HelperService } from 'src/app/service/helper.service';
import { UserToken } from '../model/user.model';

@Injectable()
export class AuthEffects {
    robRequired: boolean = false;
    robDialogRef: MatDialogRef<RobRequiredComponent>;

    passwordChangeRequired: boolean = false;
    passwordChangeRequiredDialogRef: MatDialogRef<PasswordChangeRequiredComponent>;

    otherSessionsActive: boolean = false;
    otherSessionsActiveDialogRef: MatDialogRef<OtherSessionsActiveComponent>;

    private isLoggedIn = new BehaviorSubject<boolean>(false);
    isLoggedIn$ = this.isLoggedIn.asObservable();

    private isLoggedInInitialized = new BehaviorSubject<boolean>(false);
    isLoggedInInitialized$ = this.isLoggedInInitialized.asObservable();

    private isLoggedInNavigated = new BehaviorSubject<boolean>(false);
    isLoggedInNavigated$ = this.isLoggedInNavigated.asObservable();

    private loggedInUserName = new BehaviorSubject<string>('');
    loggedInUserName$ = this.loggedInUserName.asObservable();

    navigateToNextPageTimeout: any;

    constructor(
        private actions$: Actions,
        private router: Router,
        private authService: AuthService,
        private dialog: MatDialog,
        private location: Location,
        private helperService: HelperService,
        private datePipe: DatePipe,
        private route: ActivatedRoute
    ) {
        this.isLoggedIn.next(!!localStorage.getItem('userToken'));
        this.isLoggedInInitialized.next(false);
        this.isLoggedInNavigated.next(false);
    }

    login$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(AuthActions.login),
                tap((action) => {
                    if (action?.userToken?.token) {
                        localStorage.setItem('userToken', JSON.stringify(action.userToken));
                        this.isLoggedIn.next(true);

                        let user = action.userToken.user;
                        this.loggedInUserName.next(user?.PERSON?.FIRST_NAME + ' ' + user?.PERSON?.LAST_NAME);

                        // share the token across tabs
                        window.dispatchEvent(new Event('storageEvent'));
                        //////////////////////////////

                        if (action?.userToken?.msg?.ROB_REQUIRED) {
                            this.robRequired = true;
                            if (this.robRequired) {
                                this.openRobRequiredDialog();
                            }
                        }
                        if (action?.userToken?.msg?.PASSWORD_CHANGE_REQUIRED) {
                            this.passwordChangeRequired = true;
                            if (this.passwordChangeRequired) {
                                this.openPasswordChangeRequiredDialog();
                            }
                        }
                        if (
                            action?.userToken?.msg?.OTHER_SESSIONS_ACTIVE &&
                            action?.userToken?.msg?.MAX_SESSIONS_ALLOWED &&
                            action?.userToken?.msg?.OTHER_SESSIONS_ACTIVE >= action?.userToken?.msg?.MAX_SESSIONS_ALLOWED
                        ) {
                            this.otherSessionsActive = true;
                            if (this.otherSessionsActive) {
                                this.openOtherSessionsActiveDialog(action?.userToken?.msg?.MAX_SESSIONS_ALLOWED, action?.userToken?.msg?.OTHER_SESSIONS_ACTIVE);
                            }
                        }

                        const userToken = localStorage.getItem('userToken');
                        if (userToken) {
                            this.navigateToNextPageTimeout = setTimeout(() => {
                                this.navigatePrevSessionPage(JSON.parse(userToken));
                            }, 3000);
                            this.isLoggedInInitialized.next(true);
                        }
                    } else {
                        //     localStorage.removeItem('userToken');
                        //     this.router.navigateByUrl('/login');
                        this.isLoggedInInitialized.next(false);
                    }
                })
            ),
        { dispatch: false }
    );

    tokenLogin$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(AuthActions.tokenLogin),
                tap((action) => {
                    if (action?.userToken?.token && !this.isLoggedInInitialized.value) {
                        this.tokenLogin();
                    }
                })
            ),
        { dispatch: false }
    );

    logout$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(AuthActions.logout),
                tap(() => {
                    this.authService.logout().subscribe({
                        next: (data) => {
                            localStorage.removeItem('userToken');
                            localStorage.removeItem('trackingType');
                            this.dialog.closeAll();
                            this.isLoggedIn.next(!!localStorage.getItem('userToken'));
                            this.cancelNagivateToTimeout();
                            if (window.location.pathname === '/login') {
                                window.location.reload();
                            } else {
                                this.router.navigateByUrl('/login');
                            }
                            this.isLoggedInInitialized.next(false);
                        },
                        error: (error) => {
                            console.log('error recognized');
                            console.error('Status:', error.status); // HTTP status code
                            console.error('Error body:', error.error); // Server error response
                            console.error('Full error object:', error); // Log all error data
                            this.cancelNagivateToTimeout();
                            this.isLoggedInInitialized.next(false);
                        }
                    });
                })
            ),
        { dispatch: false }
    );

    robSigned$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(AuthActions.robSigned),
                tap((action) => {
                    this.robRequired = false;
                    this.robDialogRef.close();
                })
            ),
        { dispatch: false }
    );

    robDecline$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(AuthActions.robDecline),
                tap((action) => {
                    this.robRequired = false;
                    this.robDialogRef.close();

                    this.authService.logout().subscribe({
                        next: (data) => {
                            localStorage.removeItem('userToken');
                            localStorage.removeItem('trackingType');
                            this.dialog.closeAll();
                            this.isLoggedIn.next(!!localStorage.getItem('userToken'));
                            this.cancelNagivateToTimeout();
                            if (window.location.pathname === '/login') {
                                window.location.reload();
                            } else {
                                this.router.navigateByUrl('/login');
                            }
                            this.isLoggedInInitialized.next(false);
                        },
                        error: (error) => {
                            console.log('error recognized');
                            console.error('Status:', error.status); // HTTP status code
                            console.error('Error body:', error.error); // Server error response
                            console.error('Full error object:', error); // Log all error data
                            this.cancelNagivateToTimeout();
                            this.isLoggedInInitialized.next(false);
                        }
                    });
                })
            ),
        { dispatch: false }
    );

    passwordChanged$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(AuthActions.passwordChanged),
                tap((action) => {
                    this.passwordChangeRequired = false;
                    this.passwordChangeRequiredDialogRef.close();
                })
            ),
        { dispatch: false }
    );

    removeOtherSessions$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(AuthActions.removeOtherSessions),
                tap((action) => {
                    // Set OTHER_SESSIONS_ACTIVE to 0
                    const userToken = localStorage.getItem('userToken');

                    if (userToken) {
                        let userTokenJSON = JSON.parse(userToken);
                        userTokenJSON.msg.OTHER_SESSIONS_ACTIVE = 0;

                        localStorage.setItem('userToken', JSON.stringify(userTokenJSON));
                    } else {
                        console.log('could not obtain userToken');
                    }

                    this.otherSessionsActive = false;
                    this.otherSessionsActiveDialogRef.close();
                })
            ),
        { dispatch: false }
    );

    // Modals
    openRobRequiredDialog() {
        if (this.robDialogRef && this.robDialogRef.getState() === MatDialogState.OPEN) {
            //this.robDialogRef.close();
            return;
        }

        let payload = {};

        const dialogConfig = new MatDialogConfig();
        dialogConfig.autoFocus = true;
        dialogConfig.data = payload;
        dialogConfig.maxHeight = '90vh';
        dialogConfig.disableClose = true;
        this.robDialogRef = this.dialog.open(RobRequiredComponent, dialogConfig);
    }

    openPasswordChangeRequiredDialog() {
        if (this.passwordChangeRequiredDialogRef && this.passwordChangeRequiredDialogRef.getState() === MatDialogState.OPEN) {
            //this.passwordChangeRequiredDialogRef.close();
            return;
        }

        let payload = {};

        const dialogConfig = new MatDialogConfig();
        dialogConfig.autoFocus = true;
        dialogConfig.data = payload;
        dialogConfig.minWidth = '50vw';
        dialogConfig.maxHeight = '90vh';
        dialogConfig.disableClose = true;
        this.passwordChangeRequiredDialogRef = this.dialog.open(PasswordChangeRequiredComponent, dialogConfig);
    }

    openOtherSessionsActiveDialog(MAX_SESSIONS_ALLOWED: number, OTHER_SESSIONS_ACTIVE: number) {
        if (this.otherSessionsActiveDialogRef && this.otherSessionsActiveDialogRef.getState() === MatDialogState.OPEN) {
            //this.otherSessionsActiveDialogRef.close();
            return;
        }

        let payload = { MAX_SESSIONS_ALLOWED: MAX_SESSIONS_ALLOWED, OTHER_SESSIONS_ACTIVE: OTHER_SESSIONS_ACTIVE };

        const dialogConfig = new MatDialogConfig();
        dialogConfig.autoFocus = true;
        dialogConfig.data = payload;
        dialogConfig.minWidth = '50vw';
        dialogConfig.maxHeight = '90vh';
        dialogConfig.disableClose = true;
        this.otherSessionsActiveDialogRef = this.dialog.open(OtherSessionsActiveComponent, dialogConfig);
    }

    tokenLogin() {
        this.authService.tokenLogin().subscribe((res) => {
            this.isLoggedIn.next(!!localStorage.getItem('userToken'));
            const userToken = localStorage.getItem('userToken');
            if (userToken) {
                let userTokenJSON = JSON.parse(userToken);
                userTokenJSON.msg = res.msg;

                let user = userTokenJSON.user;
                this.loggedInUserName.next(user?.PERSON?.FIRST_NAME + ' ' + user?.PERSON?.LAST_NAME);
            } else {
                console.log('could not obtain userToken');
                this.isLoggedInInitialized.next(false);
            }

            if (res.msg) {
                if (res.msg.TOKEN_EXPIRED) {
                    localStorage.removeItem('userToken');
                    this.router.navigateByUrl('/login');
                    this.dialog.closeAll();
                    //location.reload();
                } else {
                    if (res.msg.ROB_REQUIRED) {
                        this.robRequired = true;
                        if (this.robRequired) {
                            this.openRobRequiredDialog();
                        }
                    }
                    if (res.msg.PASSWORD_CHANGE_REQUIRED) {
                        this.passwordChangeRequired = true;
                        if (this.passwordChangeRequired) {
                            this.openPasswordChangeRequiredDialog();
                        }
                    }
                    if (res.msg.OTHER_SESSIONS_ACTIVE > 0 && res.msg.OTHER_SESSIONS_ACTIVE >= res.msg.MAX_SESSIONS_ALLOWED) {
                        this.otherSessionsActive = true;
                        if (this.otherSessionsActive) {
                            this.openOtherSessionsActiveDialog(res.msg.MAX_SESSIONS_ALLOWED, res.msg.OTHER_SESSIONS_ACTIVE);
                        }
                    }

                    const userToken = localStorage.getItem('userToken');
                    if (userToken) {
                        this.navigateToNextPageTimeout = setTimeout(() => {
                            this.navigatePrevSessionPage(JSON.parse(userToken), false);
                        }, 1000);
                    }
                }
            }
        });
    }

    navigatePrevSessionPage(userToken: UserToken, nagivateFlag: boolean = true) {
        const returnUrl = this.route.snapshot.queryParams['returnUrl'] || '/children/search';

        this.authService.getPreviousSessionTracking(userToken.user!.ID!.toString()).subscribe({
            next: (prevTrackType: TrackingType) => {
                if (prevTrackType.timedOut == true) {
                    let routes: Route[] = prevTrackType.routes;
                    let path: string = routes[routes.length - 1].path;

                    let newTrackingType = new TrackingType();
                    newTrackingType.id = userToken.user?.LOGIN_AUDITS?.[0].ID?.toString()!;
                    newTrackingType.loginDetailId = userToken.user?.ID?.toString()!;
                    newTrackingType.timedOut = false;

                    // 1. keep latest per path (last occurrence wins)
                    const latestByPath = Array.from(new Map(routes.map((r) => [r.path, r])).values());

                    // 2. filter rule:
                    // remove items with empty searchCriterias
                    const result = latestByPath.filter((route, index, arr) => {
                        const hasSearchCriteria = (route.searchCriterias?.length ?? 0) > 0;

                        return hasSearchCriteria;
                    });

                    newTrackingType.routes = result.slice().reverse();

                    this.helperService.setCurrSessionTracking(newTrackingType);

                    if (nagivateFlag) {
                        this.router.navigateByUrl(path);
                    } else if (window.location.pathname === '/login') {
                        this.router.navigateByUrl(returnUrl);
                    }
                } else if (userToken) {
                    if (
                        prevTrackType.loginDetailId === userToken.user?.ID?.toString() &&
                        prevTrackType.id === userToken.user?.LOGIN_AUDITS?.[0].ID?.toString()
                    ) {
                        this.helperService.setCurrSessionTracking(prevTrackType);
                        if (window.location.pathname === '/login') {
                            this.router.navigateByUrl(returnUrl);
                        }
                    } else {
                        if (nagivateFlag) {
                            this.router.navigateByUrl(returnUrl);
                        } else if (window.location.pathname === '/login') {
                            this.router.navigateByUrl(returnUrl);
                        }
                    }
                } else {
                    if (nagivateFlag) {
                        this.router.navigateByUrl(returnUrl);
                    } else if (window.location.pathname === '/login') {
                        this.router.navigateByUrl(returnUrl);
                    }
                }
            },
            error: (error) => {
                if (nagivateFlag) {
                    this.router.navigateByUrl(returnUrl);
                } else if (window.location.pathname === '/login') {
                    this.router.navigateByUrl(returnUrl);
                }
                this.isLoggedInNavigated.next(true);
            },
            complete: () => {
                this.isLoggedInNavigated.next(true);
            }
        });
    }

    cancelNagivateToTimeout(): void {
        if (this.navigateToNextPageTimeout !== undefined) {
            clearTimeout(this.navigateToNextPageTimeout);
            this.navigateToNextPageTimeout = undefined;
        }
    }
}
